<template>
  <div id="app" v-loading.fullscreen.lock="loading">
    
    <!-- 使用子组件 -->
    <SubjectBindingOverlay
      ref="subjectBindingOverlay"
      :classId="classId"
      :subjectIndex="subjectIndex"
      @binding-status-changed="handleBindingStatusChanged"
      @switch-subject="switchSubject"
    />
    
    <HeaderSection
      v-show="isSubjectBound"
      :classInfo="classInfo"
      :classId="classId"
      :currentTime="currentTime"
      :subjectIndex="subjectIndex"
    />
    
    <ControlPanel 
      ref="controlPanel"
      :currentDate="currentDate"
      :selectedType="selectedType"
      :homeworkTypes.sync="homeworkTypes"
      :homeworkContent="homeworkContent"
      :searchKeyword="searchKeyword"
      :pickerOptions="pickerOptions"
      :classId="classId"
      :subjectIndex="subjectIndex"
      :students="students"
      :correctionRecords="correctionRecords"
      :isAssigned="isAssigned"
      @date-change="onDateChange"
      @type-change="onTypeChange"
      @reset-status="loadData"
      @set-all-finished="loadData"
      @data-changed="loadData"
      @homework-assigned="handleHomeworkAssigned"
    />
    
    <div class="main-content">
      <StatisticsPanel
        ref="statisticsPanel"
        :currentDate="currentDate"
        :selectedType="selectedType"
        :classId="classId"
        :currentSubject="currentSubject"
        :subjectClass="subjectClass"
        :showUnfinishedCount="showUnfinishedCount"
        :isAssigned="isAssigned"
        @switch-subject="switchSubject"
        @toggle-unfinished-count="toggleUnfinishedCount"
        @assign-homework="onAssignHomework"
        @load-unfinished-counts="loadUnfinishedCounts"
      />
      
      <StudentGrid
        :sortedStudents.sync="students"
        :showUnfinishedCount="showUnfinishedCount"
        :studentUnfinishedCounts="studentUnfinishedCounts"
        :correctionRecords.sync="correctionRecords"
        :currentDate="currentDate"
        :selectedType="selectedType"
        :classId="classId"
        :subjectIndex="subjectIndex"
        :homeworkTypes="homeworkTypes"
        :isAssigned="isAssigned"
        :studentsLoading="studentsLoading"
        :showUnfinishedCountBadge="showUnfinishedCountBadge"
        :showContextMenu="appConfig.studentButton.showContextMenu"
        @check-student-history="handleCheckStudentHistory"
      />
      
      <Spacer 
        :classId="classId" 
        :subjectIndex="subjectIndex"
        :students="students"
      />
    </div>


    
 
  </div>
</template>

<script>
import HeaderSection from './HeaderSection.vue'
import ControlPanel from './ControlPanel.vue'
import StatisticsPanel from './StatisticsPanel.vue'
import StudentGrid from './StudentGrid.vue'
import Spacer from './Spacer.vue'
import SubjectBindingOverlay from './SubjectBindingOverlay.vue'

import Utils from '../../utils/Utils'
import EventBus from '../../utils/EventBus'
import logoutMixin from '@/mixins/logoutMixin.js'
import appConfig from '@/config/appConfig'

// 引入样式文件
import '../../assets/styles/correction-system.css'

export default {
  name: 'CorrectionSystemNew',
  mixins: [logoutMixin],
  components: {
    HeaderSection,
    ControlPanel,
    StatisticsPanel,
    StudentGrid,
    Spacer,
    SubjectBindingOverlay
  },
  props: {
    classId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      loading: true,
      currentTime: '',
      currentDate: this.getCurrentDate(),
      students: [],
      homeworkTypes: [],
      selectedType: 1,
      homeworkContent: '',
      correctionRecords: [],
      searchKeyword: '',
      studentSuggestions: [],
      subjects: ['语文', '数学', '英语'],
      subjectIndex: 0,
      // 欠交数量显示状态
      showUnfinishedCount: false,
      // 学生欠交数量数据
      studentUnfinishedCounts: {},
      // 科目绑定状态
      isSubjectBound: true,
      // 作业是否已布置
      isAssigned: true,
      // 学生数据加载状态
      studentsLoading: false,
      // 欠交数量角标显示状态
      showUnfinishedCountBadge: true,
      // 日期选择器选项
      pickerOptions: Utils.createDatepickerOptions(this),

      
      // 当前教师ID
      currentTeacherId: null,
      // 班级信息
      classInfo: null,
      // 管理弹窗相关
      managementDialogVisible: false,
      activeTab: 'class', // 管理弹窗的活动标签页
      multipleSelection: [] // 表格多选状态
    }
  },
  computed: {
    appConfig() {
      return appConfig
    },
    currentSubject() {
      return this.subjects[this.subjectIndex]
    },

    // 根据当前科目返回相应的CSS类
    subjectClass() {
      switch (this.subjectIndex) {
        case 0: // 语文 - 橙色
          return 'chinese-subject'
        case 1: // 数学 - 蓝色
          return 'math-subject'
        case 2: // 英语 - 红色
          return 'english-subject'
        default:
          return 'math-subject'
      }
    }

  },
  watch: {
    currentDate(newVal) {
      sessionStorage.setItem('selectedDate', newVal)
      // 根据当前日期设置高亮的快捷按钮
      this.setHighlightedShortcut()
    },
    selectedType(newVal) {
      sessionStorage.setItem('selectedType', newVal)
    },
    subjectIndex(newVal) {
      // 切换主题
      this.applySubjectTheme()

      // 更新URL中的subjectId参数，保持状态同步
      if (this.$route.query.subjectId !== (newVal + 1).toString()) {
        this.$router.replace({
          ...this.$route,
          query: {
            ...this.$route.query,
            subjectId: newVal + 1
          }
        }).catch(err => {
          // 忽略重复导航错误
          if (err.name !== 'NavigationDuplicated') {
            // 如果是其他错误，重新抛出
            throw err;
          }
        });
      }
    },


  },
  async mounted() {
    this.updateTime()
    setInterval(this.updateTime, 1000)
    this.loadStudents()
    
    // 监听事件总线的班级信息更新事件
    EventBus.$on('class-info-updated', this.handleClassInfoUpdated);

    // 监听作业未布置事件
    EventBus.$on('homework-unassigned', this.handleHomeworkUnassigned);

    // 监听作业类型更新事件
    EventBus.$on('homework-types-updated', this.handleHomeworkTypesUpdated);

    // 从sessionStorage恢复保存的日期、作业类型
    const savedDate = sessionStorage.getItem('selectedDate')
    const savedType = sessionStorage.getItem('selectedType')

    if (savedDate) {
      this.currentDate = savedDate
    }

    // 检查路由参数中的科目ID（优先使用URL参数）
    const routeSubjectId = this.$route.query.subjectId;
    if (routeSubjectId) {
      const subjectId = parseInt(routeSubjectId);
      if (subjectId !== 1 && subjectId !== 2 && subjectId !== 3) {
        this.logoutAndRedirect('/login');
        return;
      }
      // 将科目ID转换为索引 (1 -> 0, 2 -> 1, 3 -> 2)
      this.subjectIndex = subjectId - 1;
    } else {
      this.logoutAndRedirect('/login');
      return;
    }

    // 应用主题
    this.applySubjectTheme()

    // 先加载班级信息，再加载其他数据
    await this.loadBasicClassInfo();
    // 先加载作业类型，再加载数据
    await this.loadHomeworkTypes();
    // 恢复保存的作业类型（如果有）
    if (savedType) {
      const typeId = parseInt(savedType)
      // 确保该作业类型在当前科目下存在
      if (this.homeworkTypes.some(type => type.id === typeId)) {
        this.selectedType = typeId
      } else {
        // 如果不存在，则使用第一个作业类型
        if (this.homeworkTypes.length > 0) {
          this.selectedType = this.homeworkTypes[0].id
        }
      }
    } else {
      // 如果没有保存的类型，则使用第一个作业类型
      if (this.homeworkTypes.length > 0) {
        this.selectedType = this.homeworkTypes[0].id
      }
    }

    await this.loadData()
    // 检查当前科目的绑定状态
    if (this.$refs.subjectBindingOverlay) {
      this.$refs.subjectBindingOverlay.checkSubjectBinding();
    }
    
    // 初始化统计面板
    this.$nextTick(() => {
      this.updateStatisticsPanel();
    });
    
    // 数据加载完成，隐藏loading
    this.loading = false

    // 初始化高亮状态
    this.$nextTick(() => {
      this.setHighlightedShortcut()
    })



    // 监听窗口大小变化，确保日期选择器样式正确
    window.addEventListener('resize', () => {
      // 延迟执行以确保DOM已经更新
      setTimeout(() => {
        if (this.$refs.datePicker && this.$refs.datePicker.picker && this.$refs.datePicker.picker.visible) {
          this.setHighlightedShortcut()
        }
      }, 100)
    })

    // 阻止整个文档的右键菜单，但允许学生按钮上的右键菜单
    document.addEventListener('contextmenu', (event) => {
      // 检查右键点击是否发生在学生按钮上
      if (event.target.classList.contains('student-btn') || event.target.closest('.student-btn')) {
        // 如果是学生按钮，则不阻止默认行为，让Vue处理
        return
      }
      // 阻止其他地方的右键菜单
      event.preventDefault()
    })

    // 获取当前教师ID
    this.getCurrentTeacherId();
  },

  beforeDestroy() {
    // 移除事件总线监听器
    EventBus.$off('class-info-updated', this.handleClassInfoUpdated);
    EventBus.$off('homework-unassigned', this.handleHomeworkUnassigned);
    EventBus.$off('homework-types-updated', this.handleHomeworkTypesUpdated);
  },
  methods: {
    handleBindingStatusChanged(isBound) {
      this.isSubjectBound = isBound;
    },
    // 加载基本班级信息
    async loadBasicClassInfo() {
      try {
        if (this.classId) {
          const response = await this.$http.get(`/classes/${this.classId}`);
          if (response.data.success && response.data.class) {
            this.classInfo = response.data.class;
          }
        }
      } catch (error) {
        console.error('获取基本班级信息失败:', error);
      }
      
      // 返回Promise以确保链式调用正常工作
      return Promise.resolve();
    },
    
    // 获取当前教师ID
    async getCurrentTeacherId() {
      try {
        // 如果classId存在，通过班级和科目信息获取教师ID
        if (this.classId) {
          const response = await this.$http.get(`/teachers/getTeacherIdByClassAndSubject?classId=${this.classId}&subjectId=${this.subjectIndex + 1}`);
          if (response.data.success && response.data.teacherId) {
            this.currentTeacherId = response.data.teacherId;
          }

          // 同时获取班级基本信息用于显示标题
          await this.loadBasicClassInfo();
        }
      } catch (error) {
        console.error('获取当前教师ID失败:', error);
      }
    },





    getCurrentDate() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    
    // 根据当前日期设置高亮的快捷按钮
    setHighlightedShortcut() {
      // 这个方法在ControlPanel组件中实现，这里可以调用ControlPanel的方法
      if (this.$refs.controlPanel && typeof this.$refs.controlPanel.setHighlightedShortcut === 'function') {
        this.$refs.controlPanel.setHighlightedShortcut();
      }
    },


    updateTime() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      this.currentTime = `${year}年${month}月${day}日 ${hours}:${minutes}:${seconds}`
    },
    applySubjectTheme() {
      // 移除所有主题类
      document.body.classList.remove('chinese-theme', 'math-theme', 'english-theme')

      // 根据当前科目添加对应的主题类
      switch (this.subjectIndex) {
        case 0: // 语文 - 深黄色主题
          document.body.classList.add('chinese-theme')
          break
        case 1: // 数学 - 蓝色主题
          document.body.classList.add('math-theme')
          break
        case 2: // 英语 - 粉红色主题
          document.body.classList.add('english-theme')
          break
        default:
          document.body.classList.add('math-theme')
      }
    },

    loadStudents() {
      let url = '/students'
      if (this.classId) {
        url = `/students/class?classId=${this.classId}`
      }
      return this.$http.get(url)
          .then(response => {
            this.students = response.data
          })
    },
    loadHomeworkTypes() {
      let url = `/homeworkTypes?subjectId=${this.subjectIndex + 1}`
      if (this.classId) {
        url = `/homeworkTypes/class?classId=${this.classId}&subjectId=${this.subjectIndex + 1}`
      }
      return this.$http.get(url)
          .then(response => {
            this.homeworkTypes = response.data
            // 如果当前选中的作业类型不在新的列表中，则重置为第一个类型
            if (this.homeworkTypes.length > 0 && !this.homeworkTypes.some(type => type.id === this.selectedType)) {
              this.selectedType = this.homeworkTypes[0].id
            }
          })
    },
    async loadData() {
      this.studentsLoading = true
      
      await this.checkIsAssigned()
      
      let homeworkContentsUrl = `/homeworkContents?date=${this.currentDate}&typeId=${this.selectedType}`

      if (this.classId) {
        homeworkContentsUrl = `/homeworkContents?date=${this.currentDate}&typeId=${this.selectedType}&classId=${this.classId}`
      }

      try {
        const homeworkResponse = await this.$http.get(homeworkContentsUrl)
        
        if (homeworkResponse.status === 204) {
          this.homeworkContent = ''
        } else {
          this.homeworkContent = homeworkResponse.data ? homeworkResponse.data.content : ''
        }
      } catch (error) {
        console.error('加载作业内容失败:', error)
        this.homeworkContent = ''
      }
      
      if (this.isAssigned) {
        let correctionRecordsUrl = `/correctionRecords?date=${this.currentDate}&typeId=${this.selectedType}`

        if (this.classId) {
          correctionRecordsUrl = `/correctionRecords?date=${this.currentDate}&typeId=${this.selectedType}&classId=${this.classId}`
        }

        try {
          const correctionResponse = await this.$http.get(correctionRecordsUrl)
          this.correctionRecords = correctionResponse.data
          this.updateStatisticsPanel()
          
          if (this.showUnfinishedCount) {
            this.loadUnfinishedCounts();
          }
        } catch (error) {
          console.error('加载订正记录失败:', error)
          this.correctionRecords = []
        }
      } else {
        this.correctionRecords = []
        
        const promises = [this.updateStatisticsPanelPromise()];
        if (this.showUnfinishedCount) {
          promises.push(this.loadUnfinishedCounts());
        }
        
        Promise.all(promises).finally(() => {
        });
      }
      
      this.studentsLoading = false
      this.showUnfinishedCountBadge = true
    },

    getStudentButtonClass(student) {
      const record = this.correctionRecords.find(r => r.studentId === student.id)
      if (record && record.corrected) {
        return 'finished'
      }
      return 'unfinished'
    },
    getStudentButtonStyle() {
      // 不要在这里设置背景色和边框色，让CSS类来处理
      return {
        height: '70px',
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#333',
        width: '100%',
        boxSizing: 'border-box',
        padding: '8px',
        textAlign: 'center',
        margin: '0',
        borderRadius: '15px',
        boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    },


    async onDateChange(value) {
      this.studentsLoading = true
      this.showUnfinishedCountBadge = false
      await this.$nextTick()
      this.currentDate = value;
      await this.loadData();
      // 确保数据加载完成后强制更新子组件
      this.$forceUpdate();
      // 通知统计面板更新数据
      this.updateStatisticsPanel();
    },
    async onTypeChange(value) {
      this.studentsLoading = true
      this.showUnfinishedCountBadge = false
      await this.$nextTick()
      this.selectedType = value;
      await this.loadData();
      // 确保数据加载完成后强制更新子组件
      this.$forceUpdate();
      // 通知统计面板更新数据
      this.updateStatisticsPanel();
    },
    switchSubject() {
      this.subjectIndex = (this.subjectIndex + 1) % this.subjects.length
      this.loadHomeworkTypes().then(async () => {
        await this.loadData()
        if (this.showUnfinishedCount) {
          this.loadUnfinishedCounts()
        }

        if (this.$refs.subjectBindingOverlay) {
          this.$refs.subjectBindingOverlay.checkSubjectBinding();
        }

        const newSubjectId = this.subjectIndex + 1;
        if (parseInt(this.$route.query.subjectId) !== newSubjectId) {
          this.$router.push({
            ...this.$route,
            query: {...this.$route.query, subjectId: newSubjectId}
          }).catch(err => {
            if (err.name !== 'NavigationDuplicated' && err.name !== 'RedirectedError') {
              throw err;
            }
          });
        }
      })
      // 弹出消息提示
      this.$message({
        message: '已切换到' + this.currentSubject,
        type: 'success',
        duration: 1000
      })
    },

    toggleUnfinishedCount() {
      this.showUnfinishedCount = !this.showUnfinishedCount
      if (this.showUnfinishedCount) {
        this.loadUnfinishedCounts()
      }
    },
    
    loadUnfinishedCounts() {
      const subjectId = this.subjectIndex + 1
      let url = `/correctionRecords/unfinishedCountBySubject?subjectId=${subjectId}`
      if (this.classId) {
        url = `/correctionRecords/unfinishedCountBySubject?subjectId=${subjectId}&classId=${this.classId}`
      }
      
      this.$http.get(url)
        .then(response => {
          const counts = {}
          response.data.forEach(item => {
            counts[item.studentId] = item.unfinishedCount
          })
          this.studentUnfinishedCounts = counts
        })
        .catch(error => {
          console.error('[CorrectionSystemNew] 获取欠交数量失败:', error)
          this.$message({
            type: 'error',
            message: '获取欠交数量失败'
          })
        })
    },
    
    // 处理学生历史检查请求
    handleCheckStudentHistory(student) {
      // 调用ControlPanel中的方法
      if (this.$refs.controlPanel) {
        this.$refs.controlPanel.handleSelectStudent(student)
      }
    },
    
    // 处理作业布置成功事件
    handleHomeworkAssigned() {
      this.isAssigned = true;
      this.loadData().then(() => {
        this.updateStatisticsPanel();
        
        if (this.showUnfinishedCount) {
          this.loadUnfinishedCounts();
        }
      });
    },
    
    // 处理作业未布置事件
    handleHomeworkUnassigned() {
      this.isAssigned = false;
      this.homeworkContent = '';
      this.correctionRecords = [];
      this.updateStatisticsPanel();
    },
    
    // 处理作业类型更新事件
    async handleHomeworkTypesUpdated(eventData) {
      const { classId, subjectId, operation, data } = eventData;
      
      // 只处理当前班级和当前科目的作业类型更新
      if (classId === this.classId && subjectId === this.subjectIndex + 1) {
        // 重新加载作业类型
        await this.loadHomeworkTypes();
        // 重新加载数据
        await this.loadData();
      }
    },
    
    // 处理布置作业按钮点击
    onAssignHomework() {
      if (!this.classId || !this.currentDate || !this.selectedType) {
        this.$message.warning('参数不完整，无法布置作业');
        return;
      }
      
      this.$http.post(`/homeworkAssignments/assign?classId=${this.classId}&date=${this.currentDate}&homeworkTypeId=${this.selectedType}`)
        .then(response => {
          if (response.data.success) {
            this.$message.success('布置作业成功');
            this.handleHomeworkAssigned();
          } else {
            this.$message.error(response.data.message || '布置作业失败');
          }
        })
        .catch(error => {
          console.error('[CorrectionSystemNew] 布置作业失败:', error);
          this.$message.error('布置作业失败: ' + (error.response?.data?.message || error.message || '未知错误'));
        });
    },
    
    // 检测是否已布置作业
    async checkIsAssigned() {
      if (!this.classId || !this.currentDate || !this.selectedType) {
        this.isAssigned = true;
        return;
      }
      
      try {
        const response = await this.$http.get(`/homeworkAssignments/check?classId=${this.classId}&date=${this.currentDate}&homeworkTypeId=${this.selectedType}`);
        this.isAssigned = response.data.assigned;
      } catch (error) {
        console.error('检测作业布置状态失败:', error);
        this.isAssigned = true;
      }
    },
    
    // 主动更新统计面板的方法
    updateStatisticsPanel() {
      if (this.$refs.statisticsPanel && typeof this.$refs.statisticsPanel.updateStats === 'function') {
        this.$refs.statisticsPanel.updateStats();
      }
    },
    
    // 返回统计面板更新的Promise（用于Promise.all）
    updateStatisticsPanelPromise() {
      if (this.$refs.statisticsPanel && typeof this.$refs.statisticsPanel.updateStats === 'function') {
        return this.$refs.statisticsPanel.updateStats();
      } else {
        return Promise.resolve();
      }
    },
    
    // 处理统计数据更新（现在由StatisticsPanel自包含处理，此方法可移除）
    // handleStatsUpdated(stats) {
    //   this.finishedCount = stats.finishedCount;
    //   this.unfinishedCount = stats.unfinishedCount;
    // },
    
    // 处理表格选择变化
    handleSelectionChange(val) {
      this.multipleSelection = val;
    },
    
    // 根据作业类型获取科目ID
    getSubjectIdByHomeworkType(homeworkTypeId) {
      // 根据作业类型ID获取科目ID
      const homeworkType = this.homeworkTypes.find(type => type.id === homeworkTypeId)
      return homeworkType ? homeworkType.subjectId : 1 // 默认为语文科目
    },
    
    // 根据科目名称获取科目ID
    getSubjectIdByName(subjectName) {
      const subjectMap = {
        '语文': 1,
        '数学': 2,
        '英语': 3
      };
      return subjectMap[subjectName] || 1; // 默认为语文科目
    },
    
    // 加载班级管理数据
    loadClassManagementData() {
      // 调用ManagementDialog中的方法
      if (this.$refs.managementDialog && typeof this.$refs.managementDialog.loadClassManagementData === 'function') {
        this.$refs.managementDialog.loadClassManagementData();
      }
    },
    
    // 编辑学生
    
    // 显示管理弹窗
    showManagementDialog() {
      this.managementDialogVisible = true;
    },
    
    // 处理班级信息更新
    handleClassInfoUpdated(classInfo) {
      // 更新本地的班级信息
      this.classInfo = classInfo;
    },
    


    
  }
};
</script>

<style>
/* 引入完整的样式表 */
@import '../../assets/styles/index.css';

/* 引入订正系统专用样式 */
@import '../../assets/styles/correction-system.css';
</style>
